import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { GameState, Cell } from '../../game/types';
import { isValidMove } from '../../game/logic/board';

interface GameBoardProps {
  gameState: GameState;
  isReviewMode: boolean;
  newlyPlacedDots: Set<string>;
  fadingDots: Set<string>;
  onCellClick: (row: number, col: number) => void;
  incorrectCell?: { row: number; col: number } | null;
  illegalCell?: { row: number; col: number } | null;
  currentTheme?: string;
  /** Square to highlight while hovering a Move History entry. Does not change position. */
  historyHoverSquare?: { row: number; col: number } | null;
  /** When false, skip the faint X on illegal empty squares. Defaults to the saved Settings preference. */
  illegalHoverEnabled?: boolean;
}

type SquareMark = {
  row: number;
  col: number;
};

type ArrowMark = {
  from: { row: number; col: number };
  to: { row: number; col: number };
};

const cellKey = (row: number, col: number) => `${row}-${col}`;
const arrowKey = (a: ArrowMark) => `${a.from.row}-${a.from.col}>${a.to.row}-${a.to.col}`;

const centerPercent = (row: number, col: number) => ({
  x: ((col + 0.5) / 8) * 100,
  y: ((row + 0.5) / 8) * 100
});

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

export const GameBoard: React.FC<GameBoardProps> = ({
  gameState,
  isReviewMode,
  newlyPlacedDots,
  fadingDots,
  onCellClick,
  incorrectCell,
  illegalCell,
  currentTheme,
  historyHoverSquare = null,
  illegalHoverEnabled: illegalHoverEnabledProp
}) => {
  const [squareMarks, setSquareMarks] = useState<SquareMark[]>([]);
  const [arrows, setArrows] = useState<ArrowMark[]>([]);
  const [hoverCell, setHoverCell] = useState<{ row: number; col: number } | null>(null);
  const [storedIllegalHover] = useState(() => {
    try {
      const raw = localStorage.getItem('migoyugoIllegalHoverEnabled');
      return raw === null ? true : JSON.parse(raw) === true;
    } catch {
      return true;
    }
  });
  const illegalHoverEnabled = illegalHoverEnabledProp ?? storedIllegalHover;

  const dragFromRef = useRef<{ row: number; col: number } | null>(null);
  const dragHoverRef = useRef<{ row: number; col: number } | null>(null);
  const rightDragActive = useRef(false);
  const arrowHeadId = `annotation-arrowhead-${useId().replace(/:/g, '')}`;

  const clearAnnotations = useCallback(() => {
    setSquareMarks([]);
    setArrows([]);
    dragFromRef.current = null;
    dragHoverRef.current = null;
    rightDragActive.current = false;
  }, []);

  // Drop stale marks after a real board change
  useEffect(() => {
    clearAnnotations();
  }, [gameState.lastMove, gameState.currentPlayer, clearAnnotations]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === 'Escape') {
        clearAnnotations();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearAnnotations]);

  useEffect(() => {
    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 2 || !rightDragActive.current) {
        rightDragActive.current = false;
        return;
      }

      const from = dragFromRef.current;
      const hover = dragHoverRef.current ?? from;
      if (!from || !hover) {
        rightDragActive.current = false;
        dragFromRef.current = null;
        dragHoverRef.current = null;
        return;
      }

      if (hover.row === from.row && hover.col === from.col) {
        setSquareMarks((prev) => {
          const key = cellKey(hover.row, hover.col);
          if (prev.some((m) => cellKey(m.row, m.col) === key)) {
            return prev.filter((m) => cellKey(m.row, m.col) !== key);
          }
          return [...prev, { row: hover.row, col: hover.col }];
        });
      } else {
        const nextArrow: ArrowMark = { from, to: hover };
        setArrows((prev) => {
          const key = arrowKey(nextArrow);
          if (prev.some((a) => arrowKey(a) === key)) {
            return prev.filter((a) => arrowKey(a) !== key);
          }
          return [...prev, nextArrow];
        });
      }

      rightDragActive.current = false;
      dragFromRef.current = null;
      dragHoverRef.current = null;
    };

    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const markedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const mark of squareMarks) {
      set.add(cellKey(mark.row, mark.col));
    }
    return set;
  }, [squareMarks]);

  const handleCellMouseDown = (e: React.MouseEvent, row: number, col: number) => {
    if (e.button !== 2) return;
    e.preventDefault();
    rightDragActive.current = true;
    const pos = { row, col };
    dragFromRef.current = pos;
    dragHoverRef.current = pos;
  };

  const handleCellMouseEnter = (row: number, col: number) => {
    setHoverCell({ row, col });
    if (!rightDragActive.current) return;
    dragHoverRef.current = { row, col };
  };

  const handleBoardMouseLeave = () => {
    setHoverCell(null);
  };

  const handleLeftClick = (row: number, col: number) => {
    clearAnnotations();
    onCellClick(row, col);
  };

  const renderCell = (row: number, col: number) => {
    const cell = gameState.board[row][col];
    const key = cellKey(row, col);
    const isLastMove =
      gameState.lastMove?.row === row &&
      gameState.lastMove?.col === col;
    const isIgoCell = gameState.igoLine?.some(pos => pos.row === row && pos.col === col) || false;
    const isNewlyPlaced = newlyPlacedDots.has(key);
    const isFading = fadingDots.has(key);
    const isIncorrect = incorrectCell?.row === row && incorrectCell?.col === col;
    const isIllegal = illegalCell?.row === row && illegalCell?.col === col;
    const isHistoryHover =
      historyHoverSquare?.row === row && historyHoverSquare?.col === col;
    const isMarked = markedKeys.has(key);
    const isHoverIllegal =
      illegalHoverEnabled &&
      hoverCell?.row === row &&
      hoverCell?.col === col &&
      cell === null &&
      !isValidMove(gameState.board, row, col, gameState.currentPlayer);

    return (
      <div
        key={key}
        className={[
          'cell',
          isLastMove ? 'last-move' : '',
          isIgoCell ? 'igo-cell' : '',
          isIncorrect ? 'incorrect-move' : '',
          isIllegal ? 'illegal-move' : '',
          isHistoryHover ? 'history-hover' : '',
          isMarked ? 'preview-circle' : '',
          isHoverIllegal ? 'hover-illegal' : ''
        ].filter(Boolean).join(' ')}
        onClick={() => handleLeftClick(row, col)}
        onContextMenu={(e) => e.preventDefault()}
        onMouseDown={(e) => handleCellMouseDown(e, row, col)}
        onMouseEnter={() => handleCellMouseEnter(row, col)}
        onMouseOver={() => handleCellMouseEnter(row, col)}
      >
        {col === 0 && <div className="cell-row-label">{8 - row}</div>}
        {row === 7 && <div className="cell-col-label">{String.fromCharCode(97 + col)}</div>}
        {cell && (
          <>
            <div
              className={[
                'dot',
                cell.color,
                isNewlyPlaced ? 'new-dot' : '',
                isFading ? 'fade-out' : ''
              ].filter(Boolean).join(' ')}
            />
            {cell.isYugo && (
              <div className="yugo standard" />
            )}
          </>
        )}
        {isMarked && <div className="preview-circle-ring" aria-hidden />}
        {isHoverIllegal && !isMarked && <div className="hover-illegal-mark" aria-hidden />}
      </div>
    );
  };

  const boardStyle = currentTheme === 'wood'
    ? {
        backgroundImage: `url(${process.env.PUBLIC_URL || ''}/wood-textures/wood-board.png)`,
        backgroundSize: 'var(--board-size) var(--board-size)',
        backgroundPosition: '0 0',
        backgroundRepeat: 'no-repeat',
        backgroundOrigin: 'padding-box'
      }
    : {};

  return (
    <div
      className={`board ${isReviewMode ? 'review-mode' : 'current-state'}`}
      id="game-board"
      style={{ ...boardStyle, position: 'relative' }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseLeave={handleBoardMouseLeave}
    >
      {gameState.board.map((row, rowIndex) =>
        row.map((_: Cell | null, colIndex: number) => renderCell(rowIndex, colIndex))
      )}

      {arrows.length > 0 && (
        <svg className="board-annotations" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <defs>
            <marker
              id={arrowHeadId}
              markerWidth="4"
              markerHeight="4"
              refX="3"
              refY="2"
              orient="auto"
            >
              <path d="M0,0 L4,2 L0,4 Z" fill="rgba(21, 120, 27, 0.85)" />
            </marker>
          </defs>
          {arrows.map((arrow) => {
            const from = centerPercent(arrow.from.row, arrow.from.col);
            const to = centerPercent(arrow.to.row, arrow.to.col);
            const dx = to.x - from.x;
            const dy = to.y - from.y;
            const len = Math.hypot(dx, dy) || 1;
            const shorten = 4;
            const endX = to.x - (dx / len) * shorten;
            const endY = to.y - (dy / len) * shorten;
            const startX = from.x + (dx / len) * 2.5;
            const startY = from.y + (dy / len) * 2.5;
            return (
              <line
                key={arrowKey(arrow)}
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                className="board-annotation-arrow"
                markerEnd={`url(#${arrowHeadId})`}
              />
            );
          })}
        </svg>
      )}
    </div>
  );
};
